import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { getBackendURL } from '../utils/backendURL';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const StripePayment = ({ planCode, onSuccess, onCancel, isOverage = false, additionalExecutions = 0 }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [stripeConfig, setStripeConfig] = useState(null);
  const BACKEND_URL = getBackendURL();

  useEffect(() => {
    // Load Stripe configuration
    const loadStripeConfig = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/payments/config`);
        setStripeConfig(response.data);
      } catch (error) {
        console.error('Failed to load Stripe config:', error);
        const errorMessage = error.response?.data?.detail || 'Payment system not available. Please contact administrator.';
        setError(errorMessage);
      }
    };

    loadStripeConfig();

    // Check if returning from Stripe
    checkReturnFromStripe();
  }, []);

  const getUrlParameter = (name) => {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(window.location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  };

  const updateStatus = (message, type) => {
    setPaymentStatus({ message, type });
  };

  const checkReturnFromStripe = () => {
    const sessionId = getUrlParameter('session_id');
    if (sessionId) {
      updateStatus('Checking payment status...', 'pending');
      pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      updateStatus('Payment status check timed out. Please contact support if payment was successful.', 'error');
      return;
    }

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/status/${sessionId}`,
        { headers: getAuthHeaders() }
      );
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        updateStatus('Payment successful! Your subscription has been updated.', 'success');
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        if (onSuccess) onSuccess();
        return;
      } else if (data.status === 'expired') {
        updateStatus('Payment session expired. Please try again.', 'error');
        return;
      }

      // If payment is still pending, continue polling
      updateStatus('Payment is being processed...', 'pending');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      updateStatus('Error checking payment status. Please try again.', 'error');
    }
  };

  const initiatePayment = async () => {
    setLoading(true);
    setError('');
    setPaymentStatus(null);

    try {
      // Get current origin for success/cancel URLs
      const originUrl = window.location.origin;
      
      let requestBody;
      let endpoint;

      if (isOverage) {
        // Overage purchase
        endpoint = `${BACKEND_URL}/api/payments/overage/checkout`;
        requestBody = {
          additional_executions: additionalExecutions,
          origin_url: originUrl
        };
      } else {
        // Subscription upgrade
        endpoint = `${BACKEND_URL}/api/payments/subscription/checkout`;
        requestBody = {
          plan_code: planCode,
          origin_url: originUrl
        };
      }

      const response = await axios.post(endpoint, requestBody, {
        headers: getAuthHeaders()
      });
      const data = response.data;
      
      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  // If we're checking payment status, show status
  if (paymentStatus) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg max-w-md mx-auto">
        <div className="text-center">
          {paymentStatus.type === 'success' && <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />}
          {paymentStatus.type === 'error' && <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />}
          {paymentStatus.type === 'pending' && <Loader2 className="h-12 w-12 text-blue-500 mx-auto mb-4 animate-spin" />}
          
          <p className={`text-lg font-medium ${
            paymentStatus.type === 'success' ? 'text-green-700' :
            paymentStatus.type === 'error' ? 'text-red-700' : 
            'text-blue-700'
          }`}>
            {paymentStatus.message}
          </p>

          {paymentStatus.type === 'success' && (
            <button 
              onClick={() => window.location.href = '/'}
              className="mt-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700"
            >
              Continue to Dashboard
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg max-w-md mx-auto">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          {isOverage ? 'Purchase Additional Executions' : `Upgrade to ${planCode} Plan`}
        </h3>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">You will be redirected to Stripe to complete your payment securely.</p>
            <div className="flex items-center justify-center space-x-2">
              <div className="w-8 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">VISA</div>
              <div className="w-8 h-5 bg-red-600 rounded text-white text-xs flex items-center justify-center font-bold">MC</div>
              <div className="w-8 h-5 bg-blue-800 rounded text-white text-xs flex items-center justify-center font-bold">AMEX</div>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={initiatePayment}
              disabled={loading || !stripeConfig}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Continue to Payment'
              )}
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-4">
          🔒 Secured by Stripe • 256-bit SSL encryption
        </p>
      </div>
    </div>
  );
};

export default StripePayment;